import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useEffect, useState } from 'react';
import { FieldPath, FormProvider, UseFormReturn } from 'react-hook-form';
import {
  ApplicationInputType,
  BNPL_REQUIRE_INCOME_VERIFICATION_FINANCE_AMOUNT_THRESHOLD,
  EmploymentType,
  FINANCIAL_PRODUCT,
  IncomeVerificationType,
  Period,
  incomeVerificationFormSchema,
  isPensionerIncomeValidationRequired,
} from '@brighte/validation';
import { StepNavigator } from '@components/StepNavigator';
import { useGetOccupationDataQuery } from '@graphql/index';
import { useLaunchDarklyContextUpdate } from '@hooks';
import { useApplicationData, useOpenBankingSteps } from '@provider';
import { Text } from '@spark-web/text';
import { TextLinkButton } from '@spark-web/text-link';
import { useDisclosure } from '@spark-web/utils';
import {
  APPLICATION_STEPS,
  INCOME_VERIFICATION,
  ManualOccupationJobTitleMatchingType,
  convertAmountToMonthlyPeriod,
  getApplicantTypeAndTrackingIdFromUrl,
  getFormFields,
  isFieldExists,
  productIds,
} from '../../utils';
import EmploymentFields from './EmploymentFields';
import ManualIncomeFields from './ManualIncomeFields';
import { OpenBankingVerification } from './OpenBanking';
import {
  useBrightePayOccupationGate,
  useIncomeVerificationBeforeNextStep,
  useIncomeVerificationSummary,
} from './hooks';
import type { GateErrorFieldName } from './hooks';

type PendingGateError = {
  name: GateErrorFieldName;
  error: { type: string; message?: string } | null;
};

type IncomeVerificationContentProps = {
  formContext: UseFormReturn<IncomeVerificationType>;
  description: string;
  isOpenBankingOptionEnabled: boolean;
  financialProductId: string;
  formFields: string[];
  manualOccupationJobTitle: ManualOccupationJobTitleMatchingType;
  setManualOccupationJobTitle: React.Dispatch<React.SetStateAction<ManualOccupationJobTitleMatchingType>>;
  incomeVerification: IncomeVerificationType | undefined;
  trackingId: string;
  applicantType: string;
  setDisabledNavigation: React.Dispatch<React.SetStateAction<boolean>>;
  setIsRequestingManualIncomeEdit: (value: boolean) => void;
  showConnectedAccounts: boolean;
  setShowConnectedAccounts: React.Dispatch<React.SetStateAction<boolean>>;
  setHideSubmitButton: React.Dispatch<React.SetStateAction<boolean>>;
  isCdrChallenger: boolean;
  occupationIncomeThreshold: number;
  setOccupationIncomeThreshold: React.Dispatch<React.SetStateAction<number>>;
  useOccupationGate: boolean;
  gateResolved: boolean;
  setGateResolved: React.Dispatch<React.SetStateAction<boolean>>;
  pendingError: PendingGateError | null;
  clearPendingError: () => void;
  primaryUploadModalDisclosure: { isOpen: boolean; onOpen: () => void; onToggle: () => void };
  financeAmount: number;
  dateOfBirth: Date;
};

function IncomeVerificationContent({
  formContext,
  description,
  isOpenBankingOptionEnabled,
  financialProductId,
  formFields,
  manualOccupationJobTitle,
  setManualOccupationJobTitle,
  incomeVerification,
  trackingId,
  applicantType,
  setDisabledNavigation,
  setIsRequestingManualIncomeEdit,
  showConnectedAccounts,
  setShowConnectedAccounts,
  setHideSubmitButton,
  isCdrChallenger,
  occupationIncomeThreshold,
  setOccupationIncomeThreshold,
  useOccupationGate,
  gateResolved,
  setGateResolved,
  pendingError,
  clearPendingError,
  primaryUploadModalDisclosure,
  financeAmount,
  dateOfBirth,
}: IncomeVerificationContentProps) {
  const { watch, register, setValue } = formContext;

  const [
    applicantIncomeType,
    usingOpenBanking,
    isAdditionalIncomeWatched,
    primaryIncomeAmount,
    primaryIncomePeriod,
    applicantPensionerType,
  ] = watch([
    'applicant.employment.type',
    'applicant.usingOpenBanking',
    'applicant.income.isAdditionalIncome',
    'applicant.income.primary.amount',
    'applicant.income.primary.period',
    'applicant.employment.pensioner.pensionType',
  ]);
  const additionalFields = getFormFields(incomeVerificationFormSchema, financialProductId);
  const savedUsingOpenBanking = incomeVerification?.applicant?.usingOpenBanking;
  const resolvedUsingOpenBanking = usingOpenBanking ?? savedUsingOpenBanking;

  const monthlyIncome = convertAmountToMonthlyPeriod(primaryIncomeAmount ?? 0, primaryIncomePeriod ?? Period.Monthly);

  // The occupation lookup that hydrates `occupationIncomeThreshold` is async. Read its loading
  // state here (react-query dedupes with the call in AdditionalEmploymentFields) so we can avoid
  // evaluating the live gate against the stale initial threshold = 0, which would otherwise
  // flash the verification cards visible until the threshold resolves.
  const { isLoading: isOccupationLoading } = useGetOccupationDataQuery({}, { enabled: useOccupationGate });

  // While the gate is pending, reveal the 2-options once the user has entered an income amount
  // AND either the brighte-pay finance amount exceeds the BNPL income-verification threshold
  // (always require verification) or the typed income exceeds the occupation salary. The income
  // inputs stay mounted in ManualIncomeFields so the user can keep typing — we only ADD the
  // OpenBankingVerification cards below.
  const financeAmountRequiresVerification = financeAmount > BNPL_REQUIRE_INCOME_VERIFICATION_FINANCE_AMOUNT_THRESHOLD;
  const isPensioner = applicantIncomeType === EmploymentType.PENSIONER;
  const pensionerRequiresVerification =
    isPensioner && applicantPensionerType
      ? isPensionerIncomeValidationRequired(applicantPensionerType, dateOfBirth, monthlyIncome)
      : false;
  const liveGateRequiresVerification =
    useOccupationGate &&
    !gateResolved &&
    !isOccupationLoading &&
    monthlyIncome > 0 &&
    (financeAmountRequiresVerification ||
      (isPensioner ? pensionerRequiresVerification : monthlyIncome > occupationIncomeThreshold));

  // For brighte-pay during gate-pending: reveal the additional-income toggle inline once the
  // user has entered a primary income amount AND the 2-options are NOT visible (i.e. income
  // is within the occupation salary). When the 2-options appear, additional income is hidden
  // so the user focuses on the verification choice.
  const showAdditionalIncomeToggleDuringGate =
    useOccupationGate && !gateResolved && monthlyIncome > 0 && !liveGateRequiresVerification;

  // When the user picks one of the 2-options during gate-pending (sets usingOpenBanking),
  // mark the gate as resolved so subsequent renders use the legacy flow.
  useEffect(() => {
    if (useOccupationGate && !gateResolved && usingOpenBanking !== undefined) {
      setGateResolved(true);
    }
  }, [useOccupationGate, gateResolved, usingOpenBanking, setGateResolved]);

  useEffect(() => {
    if (useOccupationGate && !gateResolved) {
      setHideSubmitButton(liveGateRequiresVerification);
      return;
    }
    const shouldShowSubmitButton =
      (isOpenBankingOptionEnabled === true && resolvedUsingOpenBanking === false) ||
      showConnectedAccounts ||
      isOpenBankingOptionEnabled === false;
    setHideSubmitButton(!shouldShowSubmitButton);
  }, [
    isOpenBankingOptionEnabled,
    resolvedUsingOpenBanking,
    setHideSubmitButton,
    showConnectedAccounts,
    useOccupationGate,
    gateResolved,
    liveGateRequiresVerification,
  ]);

  useEffect(() => {
    if (usingOpenBanking === undefined && savedUsingOpenBanking !== undefined) {
      setValue('applicant.usingOpenBanking', savedUsingOpenBanking);
    }
  }, [savedUsingOpenBanking, setValue, usingOpenBanking]);

  // While the brighte-pay occupation gate is pending, the additional-income toggle is hidden
  // (suppressVerificationCta), so the user can't set the form value. Default it to `false` so the
  // schema's `isAdditionalIncome === undefined` check doesn't block submission and the gate can
  // run on Continue. We avoid forcing `usingOpenBanking = true` so we don't persist a CDR consent
  // the user never gave.
  useEffect(() => {
    if (useOccupationGate && !gateResolved && isAdditionalIncomeWatched === undefined) {
      setValue('applicant.income.isAdditionalIncome', false);
    }
  }, [gateResolved, isAdditionalIncomeWatched, setValue, useOccupationGate]);

  useEffect(() => {
    if (pendingError) {
      // The brighte-pay schema variant extends `employed`/`selfEmployed` with a `code` field,
      // but the form's static IncomeVerificationType does not include it — cast to satisfy
      // react-hook-form's path constraint.
      const path = pendingError.name as FieldPath<IncomeVerificationType>;
      if (pendingError.error) {
        formContext.setError(path, pendingError.error);
      } else {
        formContext.clearErrors(path);
      }
      clearPendingError();
    }
  }, [pendingError, formContext, clearPendingError]);

  //Income type is not shown for ACT_SLIP financial product by default, hence setting isApplicantIncomeTypeSelected to true
  const isApplicantIncomeTypeSelected =
    financialProductId === FINANCIAL_PRODUCT.ACT_SLIP ? true : Boolean(applicantIncomeType);

  const gatePending = useOccupationGate && !gateResolved;
  const hideTopLevelSwitcher = gatePending;

  return (
    <FormProvider {...formContext}>
      <Text>{description}</Text>
      {!hideTopLevelSwitcher && isOpenBankingOptionEnabled === true && resolvedUsingOpenBanking === false && (
        <Text>
          Want to save time?{' '}
          <TextLinkButton
            tone="primaryActive"
            weight="regular"
            onClick={() => {
              setIsRequestingManualIncomeEdit(false);
              setValue('applicant.usingOpenBanking', true);
            }}
          >
            Use fast-tracked verification
          </TextLinkButton>{' '}
          to skip these steps.
        </Text>
      )}
      {!hideTopLevelSwitcher && isOpenBankingOptionEnabled === true && resolvedUsingOpenBanking === true && (
        <Text>
          Prefer to type in the details and provide documents?{' '}
          <TextLinkButton
            tone="primaryActive"
            weight="regular"
            onClick={() => setValue('applicant.usingOpenBanking', false)}
          >
            Switch to manual verification
          </TextLinkButton>
        </Text>
      )}
      {additionalFields && isFieldExists(additionalFields, 'applicant.employment.type') && (
        <EmploymentFields financialProductId={financialProductId} />
      )}

      {/*
        ManualIncomeFields and OpenBankingVerification are rendered at fixed JSX positions so
        that transitioning between gate-pending and legacy modes does NOT remount them. With
        shouldUnregister=true on the form, remount would unregister the income period/amount
        fields, clearing those values from the form state — which would hide the post-click
        Upload documents button (its render gate checks for non-empty income).
      */}
      {(gatePending || resolvedUsingOpenBanking === false || !isOpenBankingOptionEnabled) && (
        <ManualIncomeFields
          formContext={formContext}
          formFields={formFields}
          isOpenBankingOptionEnabled={gatePending ? false : isOpenBankingOptionEnabled}
          financialProductId={financialProductId}
          manualOccupationJobTitle={manualOccupationJobTitle}
          setManualOccupationJobTitle={setManualOccupationJobTitle}
          incomeVerification={incomeVerification}
          occupationIncomeThreshold={occupationIncomeThreshold}
          setOccupationIncomeThreshold={setOccupationIncomeThreshold}
          suppressVerificationCta={gatePending}
          showAdditionalIncomeToggle={showAdditionalIncomeToggleDuringGate}
          primaryUploadModalDisclosure={useOccupationGate ? primaryUploadModalDisclosure : undefined}
        />
      )}
      {((gatePending && liveGateRequiresVerification && isOpenBankingOptionEnabled) ||
        (!gatePending && resolvedUsingOpenBanking !== false && isOpenBankingOptionEnabled)) && (
        <OpenBankingVerification
          formContext={formContext}
          trackingId={trackingId}
          isOpenBankingOptionEnabled={isOpenBankingOptionEnabled}
          incomeVerification={incomeVerification}
          setDisabledNavigation={setDisabledNavigation}
          isApplicantIncomeTypeSelected={isApplicantIncomeTypeSelected}
          onShowConnectedAccountsChange={setShowConnectedAccounts}
          isCdrChallenger={isCdrChallenger}
          onSelfDeclaredOverride={
            gatePending
              ? () => {
                  // Set gateResolved directly so we transition out of gate-pending immediately,
                  // independent of the setValue → watch propagation timing. Also commit
                  // usingOpenBanking=false so the legacy manual branch renders, and open the
                  // upload modal via the lifted disclosure so it persists across re-render.
                  setGateResolved(true);
                  setValue('applicant.usingOpenBanking', false);
                  primaryUploadModalDisclosure.onOpen();
                }
              : undefined
          }
          useOccupationGate={useOccupationGate}
        />
      )}

      {/* Hidden Fields */}
      <input type="hidden" {...register('applicant.type')} value={applicantType} />
      <input type="hidden" {...register('financialProductId')} value={financialProductId} />
      <input type="hidden" {...register('incomeVerification', { setValueAs: v => v || undefined })} />
    </FormProvider>
  );
}

function IncomeVerification() {
  const { TITLE, DESCRIPTION, DESCRIPTION_CHALLENGER } = INCOME_VERIFICATION;

  const {
    IncomeVerification: incomeVerification,
    quote,
    Identification: identification,
  } = useApplicationData(['IncomeVerification', 'quote', 'Identification']);
  const { setIsRequestingManualIncomeEdit } = useOpenBankingSteps();
  const { handleBeforeNextStep } = useIncomeVerificationBeforeNextStep();

  const [manualOccupationJobTitle, setManualOccupationJobTitle] = useState<ManualOccupationJobTitleMatchingType>({
    jobTitle: '',
    isFetched: false,
    isMatching: false,
    isMatched: true,
  });
  const [disabledNavigation, setDisabledNavigation] = useState(false);
  const [hideSubmitButton, setHideSubmitButton] = useState(false);
  const [occupationIncomeThreshold, setOccupationIncomeThreshold] = useState(0);
  const [gateResolved, setGateResolved] = useState(incomeVerification?.applicant?.usingOpenBanking !== undefined);
  const [pendingError, setPendingError] = useState<PendingGateError | null>(null);
  const primaryUploadModalDisclosure = useDisclosure();

  const { financialProduct, totalPurchaseAmount = 0, depositAmount = 0 } = quote || {};
  const financialProductId = financialProduct?.id || '';
  const financeAmount = totalPurchaseAmount - depositAmount;
  const dateOfBirth = new Date(identification?.applicant.dateOfBirth);

  const flags = useFlags();
  const updateLDContext = useLaunchDarklyContextUpdate();
  const vendorId = quote?.vendor?.publicId || '';
  const [showConnectedAccounts, setShowConnectedAccounts] = useState(false);
  const { applicantType, trackingId } = getApplicantTypeAndTrackingIdFromUrl();

  useEffect(() => {
    if (financialProductId && vendorId) {
      const context: Record<string, { key: string }> = {
        financialProduct: { key: financialProductId },
        vendorId: { key: vendorId },
      };
      if (trackingId) {
        context.trackingId = { key: trackingId };
      }
      updateLDContext(context);
    }
  }, [financialProductId, vendorId, trackingId]);

  const isOpenBankingOptionEnabled = flags['income-verification-with-cdr-option'] ?? false;
  const isCdrChallenger = flags['cdr-income-verification-ab-testing'] ?? false;
  const isBrightePayOccupationGateEnabled = flags['brightepay-income-occupation-gate'] ?? false;
  const useOccupationGate =
    isBrightePayOccupationGateEnabled && financialProductId === productIds.BRIGHTE_PAY && isOpenBankingOptionEnabled;
  const formFields = getFormFields(incomeVerificationFormSchema, financialProductId);

  // Read the occupation query loading state alongside the gate logic. react-query dedupes with
  // the call in IncomeVerificationContent and AdditionalEmploymentFields, so we don't refetch.
  // Needed here so runGateCheck / isVerificationRequired don't treat the seed value
  // `occupationIncomeThreshold === 0` as the "no salary data" sentinel before the query resolves.
  const { isLoading: isOccupationLoading } = useGetOccupationDataQuery({}, { enabled: useOccupationGate });

  const setError = useCallback((name: GateErrorFieldName, error: { type: string; message?: string } | null) => {
    setPendingError({ name, error });
  }, []);

  const clearPendingError = useCallback(() => {
    setPendingError(null);
  }, []);

  const isVerificationRequired = useCallback(
    (formValues: ApplicationInputType) => {
      if (!('applicant' in formValues) || !formValues.applicant) return false;
      if (financeAmount > BNPL_REQUIRE_INCOME_VERIFICATION_FINANCE_AMOUNT_THRESHOLD) return true;

      const amount = formValues.applicant.income?.primary?.amount ?? 0;
      const period = formValues.applicant.income?.primary?.period ?? Period.Monthly;
      const monthlyIncome = convertAmountToMonthlyPeriod(amount, period);
      const employmentType = formValues.applicant.employment?.type;

      if (employmentType === EmploymentType.PENSIONER) {
        const pensionerType = formValues.applicant.employment?.pensioner?.pensionType;
        return pensionerType ? isPensionerIncomeValidationRequired(pensionerType, dateOfBirth, monthlyIncome) : true;
      }

      // While the occupation query is loading, threshold is still the seed value 0. Treating
      // that as the "no salary" sentinel would short-circuit to verification-required without
      // ever consulting the real threshold; fall back to the verification-required default so
      // we don't prematurely clear the docs-required block on the manual path.
      if (isOccupationLoading) return true;

      return occupationIncomeThreshold === 0 ? true : monthlyIncome > occupationIncomeThreshold;
    },
    [financeAmount, isOccupationLoading, occupationIncomeThreshold, dateOfBirth],
  );

  const { runGateCheck, runDocumentCheck } = useBrightePayOccupationGate({
    occupationIncomeThreshold,
    financeAmount,
    isOccupationLoading,
    isVerificationRequired,
    setGateResolved,
    setError,
  });

  return (
    <StepNavigator<IncomeVerificationType>
      schema={incomeVerificationFormSchema}
      name={APPLICATION_STEPS.INCOME_VERIFICATION}
      title={TITLE}
      defaultValues={incomeVerification || {}}
      isLoading={false}
      completedContent={useIncomeVerificationSummary()}
      disableNavigation={
        !manualOccupationJobTitle.isMatched ||
        disabledNavigation ||
        (useOccupationGate && !gateResolved && isOccupationLoading)
      }
      onValidateBeforeSave={formValues => {
        // Run the brighte-pay occupation/document gate BEFORE the backend save so an invalid
        // payload never reaches updateApplication. The gate is two-step: the first Continue
        // resolves the occupation threshold (runGateCheck sets gateResolved and blocks), a later
        // Continue enforces documents (runDocumentCheck). Blocking here sets the relevant field
        // errors and short-circuits the save; the save only runs once the gate fully passes.
        if (useOccupationGate) {
          if (!gateResolved) {
            return runGateCheck(formValues);
          }
          return runDocumentCheck(formValues);
        }
        return false;
      }}
      onBeforeNextStep={formValues => handleBeforeNextStep(formValues, manualOccupationJobTitle.isMatched)}
      hideSubmitButton={hideSubmitButton}
    >
      {(formContext: UseFormReturn<IncomeVerificationType>) => (
        <IncomeVerificationContent
          formContext={formContext}
          description={isCdrChallenger ? DESCRIPTION_CHALLENGER : DESCRIPTION}
          isOpenBankingOptionEnabled={isOpenBankingOptionEnabled}
          financialProductId={financialProductId}
          formFields={formFields}
          manualOccupationJobTitle={manualOccupationJobTitle}
          setManualOccupationJobTitle={setManualOccupationJobTitle}
          incomeVerification={incomeVerification}
          trackingId={trackingId}
          applicantType={applicantType}
          setDisabledNavigation={setDisabledNavigation}
          setIsRequestingManualIncomeEdit={setIsRequestingManualIncomeEdit}
          showConnectedAccounts={showConnectedAccounts}
          setShowConnectedAccounts={setShowConnectedAccounts}
          setHideSubmitButton={setHideSubmitButton}
          isCdrChallenger={isCdrChallenger}
          occupationIncomeThreshold={occupationIncomeThreshold}
          setOccupationIncomeThreshold={setOccupationIncomeThreshold}
          useOccupationGate={useOccupationGate}
          gateResolved={gateResolved}
          setGateResolved={setGateResolved}
          pendingError={pendingError}
          clearPendingError={clearPendingError}
          primaryUploadModalDisclosure={primaryUploadModalDisclosure}
          financeAmount={financeAmount}
          dateOfBirth={dateOfBirth}
        />
      )}
    </StepNavigator>
  );
}

export default IncomeVerification;
